// Small shared pieces. Every interactive element is a real <a>, <button> or form control.

import type { ActorId, Claim } from '@model/index.ts';
import { html, raw } from './html.ts';
import type { Trusted } from './html.ts';
import { lang, t, tn } from './i18n.ts';
import type { Key } from './i18n.ts';
import { completedCount, num, str } from './queries.ts';
import { world as w } from './world.ts';
import { fmtAgo, fmtPrice } from './format.ts';
import type { Why } from './explain.ts';
import { itemArt } from './art.ts';
import { BLOCK } from './cast.ts';

// The block a person lives in, if the colony knows it.
export function blockOf(id: ActorId): string {
  const key = w.byId.get(id)?.key ?? '';
  return BLOCK[key]?.[lang()] ?? '';
}

// A person's initials on a neutral disc: no faces, no avatars.
export function monogram(id: ActorId): Trusted {
  const letter = [...w.name(id).trim()][0] ?? '?';
  return html`<span class="mono-disc" aria-hidden="true">${letter}</span>`;
}

export const personHref = (id: ActorId): string => `#/person/${w.byId.get(id)?.key ?? ''}`;

export function back(href: string, label: Key = 'common.back'): Trusted {
  return html`<a class="back" href="${href}"><svg aria-hidden="true" viewBox="0 0 24 24"><path d="M15 5l-7 7 7 7"/></svg>${t(label)}</a>`;
}

export function tradesLine(id: ActorId): string {
  return tn('trades', completedCount(w, id));
}

export function itemRow(o: Claim, opts: { showSeller?: boolean } = {}): Trusted {
  const seller = opts.showSeller !== false;
  const who = o.author === w.me.id ? t('common.you') : w.name(o.author);
  const where = blockOf(o.author);
  return html`<li class="card">
    <a class="card-link" href="#/item/${o.id}">
      ${itemArt(str(o, 'title'), 'tile', str(o, 'kind'))}
      <span class="card-price">${fmtPrice(num(o, 'priceInr'))}</span>
      <span class="card-title">${str(o, 'title')}</span>
      <span class="card-meta">${seller ? `${who}${where ? ` · ${where}` : ''}` : `${where ? `${where} · ` : ''}${fmtAgo(o.issuedAt, w.now())}`}</span>
      ${seller ? html`<span class="card-standing">${tradesLine(o.author)}</span>` : ''}
    </a>
  </li>`;
}

export function primary(label: string, attrs: Record<string, string> = {}): Trusted {
  const a = Object.entries(attrs)
    .map(([k, v]) => html` ${raw(k)}="${v}"`.value)
    .join('');
  return raw(`<button class="btn" type="${attrs.type ?? 'button'}"${a}>${html`${label}`.value}</button>`);
}

export function linkButton(label: string, href: string, kind: 'btn' | 'quiet' = 'btn'): Trusted {
  return html`<a class="${kind}" href="${href}">${label}</a>`;
}

export function listen(text: string): Trusted {
  if (!('speechSynthesis' in window)) return html``;
  return html`<button class="quiet listen" type="button" data-act="listen" data-text="${text}">
    <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M4 9v6h4l5 4V5L8 9H4z"/><path d="M16.5 8.5a5 5 0 010 7"/></svg>${t('common.listen')}</button>`;
}

// The reason card: a denial in plain words, with the evidence a person can act on.
export function whyCard(why: Why, title: string, opts: { takeDown?: boolean } = {}): Trusted {
  const spoken = [title, why.headline, ...why.items.map((i) => str(i, 'title')), ...why.lines].join('. ');
  const actions: string[] = [];
  if (why.actions.includes('takeDown') && why.items.length) actions.push(t('do.takeDown'));
  if (why.actions.includes('ramp') && why.rampTo) actions.push(t('do.ramp', { limit: why.rampTo, days: Math.round(Number(w.scope.policy.params.rampAfter ?? 0) / 86_400_000) }));
  if (why.actions.includes('wait')) actions.push(t('do.wait'));
  return html`<section class="why" aria-labelledby="why-title" data-why="${why.label ?? ''}">
    <h2 id="why-title">${title}</h2>
    ${why.headline !== title ? html`<p class="why-lead">${why.headline}</p>` : ''}
    ${why.items.length
      ? html`<ul class="why-items">
          ${why.items.map(
            (o) => html`<li>
              ${itemArt(str(o, 'title'), 'thumb', str(o, 'kind'))}
              <a href="#/item/${o.id}">${str(o, 'title')}</a>
              <span class="item-price">${fmtPrice(num(o, 'priceInr'))}</span>
              ${opts.takeDown ? html`<button class="quiet small" type="button" data-act="withdraw" data-id="${o.id}">${t('item.takeDown')}</button>` : ''}
            </li>`,
          )}
        </ul>`
      : ''}
    ${why.lines.map((l) => html`<p>${l}</p>`)}
    ${actions.length ? html`<h3>${t('why.what')}</h3><ol class="why-do">${actions.map((a) => html`<li>${a}</li>`)}</ol>` : ''}
    ${why.actions.includes('join') ? linkButton(t('do.join'), '#/') : ''}
    ${listen(spoken)}
  </section>`;
}

export function icon(name: 'nearby' | 'sell' | 'chats' | 'me' | 'cases' | 'rules' | 'lock' | 'check'): Trusted {
  const paths: Record<string, string> = {
    nearby: '<path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0113 0c0 5.4-6.5 11-6.5 11z"/><circle cx="12" cy="10" r="2.4"/>',
    sell: '<path d="M3.5 12.5l8-8H20v8.5l-8 8z"/><circle cx="15.6" cy="8.4" r="1.4"/>',
    chats: '<path d="M4 5.5h16v10H9l-5 4z"/>',
    me: '<circle cx="12" cy="8.5" r="3.6"/><path d="M4.5 20c1.2-4 4.1-5.8 7.5-5.8s6.3 1.8 7.5 5.8"/>',
    cases: '<path d="M4 7h16v12H4z"/><path d="M9 7V4.5h6V7"/><path d="M4 12h16"/>',
    rules: '<path d="M6 3.5h9l3 3V20.5H6z"/><path d="M9 10h6M9 13.5h6M9 17h4"/>',
    lock: '<rect x="5" y="10.5" width="14" height="10" rx="2"/><path d="M8.5 10.5V8a3.5 3.5 0 017 0v2.5"/>',
    check: '<path d="M5 12.5l4.5 4.5L19 7.5"/>',
  };
  return raw(`<svg class="icon" aria-hidden="true" viewBox="0 0 24 24">${paths[name]}</svg>`);
}
